import { Component, ViewChild } from '@angular/core';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { ListContactComponent } from 'components/company/contact/list-contact.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { ListViewComponent } from 'components/layout/list-view/list-view.component';
import { StorageContainer } from 'constants/StorageContainer';
import { InputStateDirective } from 'directives/input-state.directive';
import { CredentialType } from 'enums/CredentialType';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { DebtCollectionDtoModel } from 'models/generated/Accounting.Api.Models.DebtCollectionReadyInvoicesDto';
import { BillingInfoDtoModel } from 'models/generated/Models.BillingInfoDto';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyService } from 'services/company.service';
import { CredentialRequired, CredentialService } from 'services/credential.service';
import { DebtCollectionService } from 'services/debt-collection.service';
import { FileStorageService } from 'services/file-storage.service';
import { ComponentUrl } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { BcDocumentType } from 'enums/BcDocumentType';
import { BrandService } from 'services/brand.service';

@Component({
	selector: 'invoice-overdue-search',
	templateUrl: './invoice-overdue-search.component.html',
	imports: [Layout, CustomSelectComponent, ListView, InputStateDirective, CompanyInformationComponent, ListContactComponent, CurrencyPipe, TranslatePipe, CustomDatePipe]
})
@ComponentUrl('invoice-overdue-search')
@CredentialRequired(CredentialType.DebtCollection)
export class InvoiceOverdueSearchComponent {
	@ViewChild('searchButton') searchButton!: SubmitButtonComponent;
	@ViewChild('exportCsvAndRegisterInvoicesButton') exportCsvAndRegisterInvoicesButton!: SubmitButtonComponent;
	@ViewChild('exportCsvButton') exportCsvButton!: SubmitButtonComponent;
	@ViewChild('overdueInvoicesList') overdueInvoicesList?: ListViewComponent;

	debtCollectionReadyInvoices?: DebtCollectionDtoModel[];
	searching = false;
	selectedInvoice?: DebtCollectionDtoModel | null;
	clickedSelectedInvoices: DebtCollectionDtoModel[] = [];
	company?: CompanyModel;
	billingInfo?: BillingInfoDtoModel;
	reasons: ReasonModel[] = [];
	selectedCulture = 'da-DK';
	cultureList: SelectListItemModel[] = [];
	debtCollectionRegisterAndExportCredential = CredentialType.DebtCollectionRegisterAndExport;
	brandId: string;

	constructor(private readonly debtCollectionService: DebtCollectionService,
		private readonly fileStorageService: FileStorageService,
		private readonly companyService: CompanyService,
		private readonly notificationService: NotificationService,
		private readonly translate: TranslatePipe,
		private readonly date: CustomDatePipe,
		private readonly credentialService: CredentialService,
		brandService: BrandService) {

		const brandId = brandService.brands.find(b => b.shortName == 'GO')?.id;
		if (!brandId) throw new Error('Unable to find GO brand');

		this.brandId = brandId;
		this.cultureList = ['da-DK', 'en-US'].map(culture => new SelectListItemModel(culture, this.translate.transform('general.language.' + culture)));
	}

	async search() {
		this.searching = true;
		try {
			this.debtCollectionReadyInvoices = await this.debtCollectionService.getInvoicesReadyForDebtCollection(this.brandId, this.searchButton);
		}
		catch (error) {
			this.searchButton.reset();
		}
		this.searching = false;
	}

	selectCompany(invoice: DebtCollectionDtoModel) {
		if (this.selectedInvoice && this.selectedInvoice.companyId == invoice.companyId) return;
		this.company = undefined;
		this.selectedInvoice = invoice;
		this.companyService.getCompany(this.selectedInvoice!.companyId!).then(company => {
			this.company = company;
		});
		this.companyService.getCompanyBillingInfo(this.selectedInvoice!.companyId!).then(info => {
			this.billingInfo = info;
		});
	}

	selectAll(event: MouseEvent) {
		let select = (event.target as HTMLInputElement).checked;
		if (select && this.overdueInvoicesList) {
			const data = this.overdueInvoicesList.getFilteredListData();
			if (data)
				this.clickedSelectedInvoices = [...data as DebtCollectionDtoModel[]];
		}
		else this.clickedSelectedInvoices = [];
	}

	toggleClickSelect(invoice: DebtCollectionDtoModel) {
		if (this.clickedSelectedInvoices.indexOf(invoice) >= 0) {
			this.clickedSelectedInvoices.splice(this.clickedSelectedInvoices.indexOf(invoice), 1);
		}
		else {
			this.clickedSelectedInvoices.push(invoice);
		}
	}

	removeSelectedInvoice(invoice: DebtCollectionDtoModel) {
		if (this.clickedSelectedInvoices.indexOf(invoice) >= 0) {
			this.clickedSelectedInvoices.splice(this.clickedSelectedInvoices.indexOf(invoice), 1);
		}
	}

	async exportCsvAndRegisterInvoicesForDebtCollection() {
		if (!this.credentialService.hasCredentials(this.debtCollectionRegisterAndExportCredential))
			return;
		if (!await firstValueFrom(this.notificationService.confirm('invoice-overdue-search.export-csv-for-debt-collection.confirm'))) {
			this.exportCsvAndRegisterInvoicesButton.reset();
			return;
		}
		const [headers, rows, filename] = this.getCsvData();
		const blob = this.fileStorageService.getCsv(headers, rows);

		await this.debtCollectionService.registerInvoicesForDebtCollection(this.clickedSelectedInvoices)

		const fileReference = (await this.fileStorageService.getFileReferencesForUpload([filename], StorageContainer.debtCollection))[0];

		try {
			const upload = this.fileStorageService.uploadFile(fileReference.uploadUrl!, new File([blob], filename));
			await lastValueFrom(upload);
			await this.fileStorageService.finalizeUploads([fileReference.id!]);
			await this.fileStorageService.downloadFile(fileReference.filePath!, StorageContainer.debtCollection);
		}
		catch (error) {
			// something is wrong client download file and inform user that upload failed, but registration succeeded
			this.fileStorageService.downloadCsv(headers, rows, filename);
			console.error(error);
			if (error instanceof Error) this.notificationService.error('error.upload', error.message);
			else this.notificationService.error('error.upload', 'Upload failed, but debt collection registration are completed, save file manually to storage account required.');
		}

		this.clickedSelectedInvoices = [];
		this.selectedInvoice = undefined;
		this.exportCsvAndRegisterInvoicesButton.reset();
		this.debtCollectionReadyInvoices = undefined;
	}

	exportCsv() {
		var [headers, rows, filename] = this.getCsvData();
		this.fileStorageService.downloadCsv(headers, rows, filename);
		this.exportCsvButton.reset();
	}

	private getCsvData(): [string[], string[][], string] {
		const rows: string[][] = [];

		this.clickedSelectedInvoices.forEach(invoice => {
			rows.push([
				invoice.vatNumber,
				invoice.documentNo,
				invoice.customerNo,
				invoice.count,
				this.date.transform(invoice.postingDate, 'iso8601'),
				BcDocumentType[invoice.documentType],
				Intl.NumberFormat(this.selectedCulture).format(invoice.originalAmountInclVat),
				Intl.NumberFormat(this.selectedCulture).format(invoice.remainingAmountInclVat),
				this.date.transform(invoice.originalDueDate, 'iso8601'),
				invoice.companyName,
				invoice.address,
				invoice.city,
				invoice.contact,
				invoice.zipcode,
				invoice.emailAddress,
				invoice.businessStatus,
				invoice.objectionReasonName,
				''
			]);
		});

		const headers = [
			this.translate.transform('debt-collection.csv-header.vatnumber'),
			this.translate.transform('debt-collection.csv-header.documentnumber'),
			this.translate.transform('debt-collection.csv-header.customerno'),
			this.translate.transform('debt-collection.csv-header.count'),
			this.translate.transform('debt-collection.csv-header.postingdate'),
			this.translate.transform('debt-collection.csv-header.document-type'),
			this.translate.transform('debt-collection.csv-header.original-amount-incl-vat'),
			this.translate.transform('debt-collection.csv-header.remaining-amount-incl-vat'),
			this.translate.transform('debt-collection.csv-header.original-due-date'),
			this.translate.transform('debt-collection.csv-header.company-name'),
			this.translate.transform('debt-collection.csv-header.address'),
			this.translate.transform('debt-collection.csv-header.city'),
			this.translate.transform('debt-collection.csv-header.contact'),
			this.translate.transform('debt-collection.csv-header.zipcode'),
			this.translate.transform('debt-collection.csv-header.email'),
			this.translate.transform('debt-collection.csv-header.business-status'),
			this.translate.transform('debt-collection.csv-header.objection-reason-name'),
			this.translate.transform('debt-collection.csv-header.contact-from-lawyer')
		];
		return [headers, rows, `export_${this.date.transform(new Date(), 'stripped')}.csv`];
	}
}
